// AGC: TransferStockModal
// AGC: Move inventory stock from one warehouse to another atomically.
// AGC: Shows transfer correlation ID after success for audit reference.

import React, { useState, useCallback } from 'react'
import { X, ArrowRight, Loader2, Check, ClipboardCopy, Package } from 'lucide-react'
import type { Warehouse, InventoryItem } from '../../types/inventory'
import type { UseWarehouseReturn } from '../../hooks/useWarehouse'
import { clampDecimalStr } from '../../utils'
import toast from 'react-hot-toast'

interface TransferStockModalProps {
  isOpen: boolean
  onClose: () => void
  warehouses: Warehouse[]
  inventoryItems: InventoryItem[]
  defaultItemId?: string
  defaultFromWarehouseId?: string
  warehouseHook: UseWarehouseReturn
}

export const TransferStockModal: React.FC<TransferStockModalProps> = ({
  isOpen,
  onClose,
  warehouses,
  inventoryItems,
  defaultItemId,
  defaultFromWarehouseId,
  warehouseHook,
}) => {
  const [itemId, setItemId] = useState(defaultItemId || '')
  const [fromWarehouseId, setFromWarehouseId] = useState(defaultFromWarehouseId || warehouses[0]?.id || '')
  const [toWarehouseId, setToWarehouseId] = useState('')
  const [qty, setQty] = useState('')
  const [qtyUnit, setQtyUnit] = useState('kg')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [completedTransferId, setCompletedTransferId] = useState<string | null>(null)

  const { transferItem } = warehouseHook

  const selectedItem = inventoryItems.find(i => i.id === itemId)
  const availableUnits = selectedItem?.unit ? [selectedItem.unit, 'kg', 'ton', 'sacas', 'L'] : ['kg', 'ton', 'L', 'sacas']

  const handleTransfer = useCallback(async () => {
    if (!itemId) { toast.error('Select an item'); return }
    if (!fromWarehouseId || !toWarehouseId) { toast.error('Select source and destination warehouses'); return }
    if (fromWarehouseId === toWarehouseId) { toast.error('Source and destination must be different'); return }
    const amount = parseFloat(qty)
    if (!amount || amount <= 0) { toast.error('Enter a valid quantity'); return }

    setLoading(true)
    try {
      const transferId = await transferItem({
        itemId,
        fromWarehouseId,
        toWarehouseId,
        quantity: amount,
        quantityUnit: qtyUnit,
        reason: reason || 'Warehouse transfer',
      })
      setCompletedTransferId(transferId)
      toast.success('Transfer completed!')
    } catch (err: any) {
      toast.error(err.message || 'Transfer failed')
    } finally {
      setLoading(false)
    }
  }, [itemId, fromWarehouseId, toWarehouseId, qty, qtyUnit, reason, transferItem])

  const handleCopyId = useCallback(() => {
    if (completedTransferId) {
      navigator.clipboard.writeText(completedTransferId).then(() => toast.success('Transfer ID copied!'))
    }
  }, [completedTransferId])

  if (!isOpen) return null

  const fromWarehouse = warehouses.find(w => w.id === fromWarehouseId)
  const toWarehouse = warehouses.find(w => w.id === toWarehouseId)

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} role="button" aria-label="Close" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClose()} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold text-gray-900">Transfer Stock</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* AGC: Success state */}
          {completedTransferId ? (
            <div className="text-center py-4 space-y-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Transfer Complete!</p>
                <p className="text-sm text-gray-500 mt-1">
                  {qty} {qtyUnit} moved from {fromWarehouse?.name} → {toWarehouse?.name}
                </p>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                <code className="text-xs text-gray-600 flex-1 truncate">{completedTransferId}</code>
                <button onClick={handleCopyId} className="p-1 hover:bg-gray-200 rounded text-gray-500" title="Copy transfer ID">
                  <ClipboardCopy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-400">Transfer ID for audit reference</p>
              <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition-colors">
                Done
              </button>
            </div>
          ) : (
            <>
              {/* AGC: Item selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Package className="w-3.5 h-3.5 inline mr-1" />
                  Item to Transfer *
                </label>
                <select
                  value={itemId}
                  onChange={e => setItemId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select item...</option>
                  {inventoryItems.map(item => (
                    <option key={item.id} value={item.id}>{item.name} ({item.unit})</option>
                  ))}
                </select>
              </div>

              {/* AGC: Warehouse route */}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <select
                    value={fromWarehouseId}
                    onChange={e => setFromWarehouseId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {warehouses.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 shrink-0 mt-5" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <select
                    value={toWarehouseId}
                    onChange={e => setToWarehouseId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {warehouses.filter(wh => wh.id !== fromWarehouseId).map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* AGC: Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={qty}
                    onChange={e => setQty(clampDecimalStr(e.target.value))}
                    placeholder="Amount"
                    min={0.001}
                    step="0.01"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={qtyUnit}
                    onChange={e => setQtyUnit(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[...new Set(availableUnits)].map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              {/* AGC: Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Seasonal restock, overflow"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                onClick={handleTransfer}
                disabled={loading || !itemId || !fromWarehouseId || !toWarehouseId || fromWarehouseId === toWarehouseId || !qty || parseFloat(qty) <= 0}
                className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Transfer Stock
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default TransferStockModal
